<template>
  <div>
    <v-container
      fluid
      :class="{
        'mx-12': !mobile,
        'my-6': !mobile,
        'mx-0': mobile,
        'my-3': mobile
      }"
    >
      <div class="card-label d-flex flex-row mb-4">
        <v-icon>mdi-alert-outline</v-icon>
        <p class="mb-0 ml-4 subtitle-1 font-weight-bold">Violations</p>
      </div>
      <v-text-field
        v-model="searchString"
        solo
        flat
        append-icon="mdi-magnify"
        label="Search Tenant"
        hide-details
        class="mb-6"
        @click:append="searchAction"
        @keydown.enter="searchAction"
      >
      </v-text-field>
      <v-card
        v-if="access.read"
        v-show="!loading"
        elevation="0"
        :class="{
          'px-8': !mobile,
          'py-4': !mobile,
          'px-2': mobile,
          'pb-2': mobile
        }"
      >
        <v-data-table
          :search="searchString"
          :headers="headers"
          :items="violations"
          :options.sync="options"
          :server-items-length="total"
          item-key="refNo"
          :loading="loading"
          loading-text="Loading... Please wait"
          multi-sort
          no-data-text="All tenant violations will appear here."
          dense
          :footer-props="{
            'items-per-page-options': !mobile ? [10, 20, 50, 100] : []
          }"
          mobile-breakpoint="600"
          :disable-sort="mobile"
        >
          <template v-slot:item="{ expand, item, isExpanded }">
            <tr v-if="!mobile" class="tr-head">
              <td>
                <p class="caption">{{ item.refNo }}</p>
              </td>
              <td>
                <p class="caption">{{ item.date }}</p>
              </td>
              <td class="text-overflow">
                <a class="caption" @click="redirectBed(item)">{{
                  item.tenantName
                }}</a>
              </td>
              <td>
                <p class="caption">{{ item.description }}</p>
              </td>
              <td>
                <p class="caption">
                  {{ item.payment_details && item.payment_details.refNo }}
                </p>
              </td>
              <td>
                <div v-if="item.attachments && item.attachments.length > 0">
                  <v-btn icon @click="downloadAttachments(item.attachments)">
                    <v-icon regular>mdi-attachment</v-icon>
                  </v-btn>
                </div>
              </td>
            </tr>
            <tr v-else @click="showViolation(item)">
              <td class="d-flex flex-row align-center py-2">
                <p class="caption font-weight-bold mr-6">
                  No. {{ item.refNo }}
                </p>
                <a class="caption">{{ item.tenantName }}</a>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
      <v-dialog v-if="dialog" v-model="dialog">
        <v-card>
          <v-card-title class="pb-0">
            <v-row>
              <v-col cols="1" class="mr-5 py-0"></v-col>
              <v-col class="pb-1 pl-0">
                <p class="font-weight-bold">Violation No. {{ item.refNo }}</p>
              </v-col>
            </v-row>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="1" class="mr-6 py-0">
                <v-icon medium>mdi-account-circle</v-icon>
              </v-col>
              <v-col class="py-0">
                <v-row>
                  <p class="font-weight-bold subtitle-2">
                    <a @click="redirectBed(item)">{{ item.userName }}</a>
                  </p>
                </v-row>
                <v-row>
                  <p class="caption">
                    <span class="font-weight-bold"
                      >Unit: {{ item.bedName }}
                    </span>
                  </p>
                </v-row>
                <v-row>
                  <p class="caption">
                    <span class="font-weight-bold">
                      Alias: {{ item.tenantName }}
                    </span>
                  </p>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="mt-3">
              <v-col cols="1" class="mr-6 py-0">
                <v-icon medium>mdi-calendar-month-outline</v-icon>
              </v-col>
              <v-col class="py-0">
                <v-row>
                  <p class="font-weight-bold subtitle-2">Date</p>
                </v-row>
                <v-row>
                  <p class="caption">
                    <span class="font-weight-bold"> {{ item.date }} </span>
                  </p>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="mt-3">
              <v-col cols="1" class="ml-3 mr-3 py-0">
                <v-row>
                  <v-icon>mdi-cash-multiple</v-icon>
                </v-row>
              </v-col>
              <v-col class="py-0">
                <v-row>
                  <p class="font-weight-bold subtitle-2">Payment Info</p>
                </v-row>
                <v-row>
                  <p class="caption">
                    Total:
                    <span class="font-weight-bold">{{
                      formatCurrency(item.payment_details.total)
                    }}</span>
                  </p> </v-row
                ><v-row>
                  <p class="caption">
                    Amount paid:
                    <span class="font-weight-bold">{{
                      formatCurrency(item.payment_details.amountPaid)
                    }}</span>
                  </p>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="mt-3">
              <v-col cols="1" class="ml-3 mr-3 py-0">
                <v-row>
                  <v-icon medium>mdi-text</v-icon>
                </v-row>
              </v-col>
              <v-col class="py-0">
                <v-row>
                  <p class="font-weight-bold subtitle-2">Description</p>
                </v-row>
                <v-row>
                  <p class="caption">{{ item.description }}</p>
                </v-row>
              </v-col>
            </v-row>
            <v-row class="mt-3">
              <v-col cols="1" class="ml-3 mr-3 py-0">
                <v-row>
                  <v-icon medium>mdi-attachment</v-icon>
                </v-row>
              </v-col>
              <v-col class="py-0">
                <v-row>
                  <p class="font-weight-bold subtitle-2">Attachments</p>
                </v-row>
                <v-row>
                  <v-card v-if="!loading" class="elevation-0">
                    <div>
                      <v-chip-group multiple column class="pt-0">
                        <v-chip
                          v-for="(attachment, index) in item.attachments"
                          :key="index"
                          outlined
                          color="primary"
                          :href="attachment.url"
                          class="my-1"
                        >
                          <div class="chip-label">
                            {{ attachment.filename }}
                          </div>
                        </v-chip>
                      </v-chip-group>
                    </div>
                  </v-card>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-skeleton-loader
        v-show="loading"
        ref="skeleton"
        type="table"
        loading
        class="mx-auto"
      ></v-skeleton-loader>
    </v-container>
    <!-- <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay> -->
  </div>
</template>

<script>
import Hashids from 'hashids'
const hashids = new Hashids()
export default {
  data() {
    return {
      searchString: '',
      loading: true,
      limit: 0,
      total: 0,
      options: {},
      dialog: false,
      item: {}
    }
  },
  computed: {
    violations() {
      return this.$store.getters['violations/getViolations']
    },
    headers() {
      return this.$store.getters['violations/getHeaders']
    },
    access() {
      return this.$root.$roleAccess('violations').access
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    }
  },
  watch: {
    options: {
      handler() {
        this.$store.dispatch('violations/fetchViolationsAction', {
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy,
          sortDesc: this.options.sortDesc,
          search: this.searchString
        })
      }
    }
  },
  mounted() {
    this.$store
      .dispatch('violations/fetchViolationsAction', {
        page: 1,
        limit: this.options.itemsPerPage
      })
      .then((data) => {
        this.loading = false
        this.limit = data.limit
        this.total = data.count
      })
  },
  methods: {
    showViolation: function (item) {
      this.item = item
      this.dialog = true
    },
    test: function (props) {
      //eslint-disable-next-line
      console.log(props)
    },
    downloadAttachments(attachments) {
      for (let i = 0; i < attachments.length; i++) {
        window.open(attachments[i].url)
      }
    },
    async searchAction() {
      this.loading = true
      await this.$store
        .dispatch('violations/fetchViolationsAction', {
          page: this.options.page,
          limit: this.options.itemsPerPage,
          sortBy: this.options.sortBy,
          sortDesc: this.options.sortDesc,
          search: this.searchString
        })
        .then(() => {
          this.loading = false
        })
    },
    redirectBed(violation) {
      const { bedId, username } = violation
      if (bedId != null) this.$router.push(`/bed/${hashids.encode(bedId)}`)
      else this.$router.push(`/user/${username}`)
    },
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    }
  }
}
</script>

<style scoped>
tr.tr-head {
  cursor: pointer;
}
.v-card {
  border-radius: 12px !important;
}
.v-input {
  border-radius: 10px;
}
table tbody tr td {
  /*border: none !important;
    border-bottom: none !important;*/
  border-color: #eeeeee !important;
}
p {
  margin-bottom: 0;
}
.text-overflow {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/*.tr-head {
  border-bottom: 0px;
}
.tr-attachments {
  border-top: 0px;
}*/
</style>
